import { api } from '../client';

// Types
export interface GoodsReceiptItem {
  id: string;
  goodsReceiptId: string;
  purchaseOrderItemId?: string;
  shipmentItemId?: string;
  expectedQty: number;
  receivedQty: number;
  acceptedQty: number;
  rejectedQty: number;
  rejectionReason?: string;
  batchNumber?: string;
  expiryDate?: string;
  serialNumbers: string[];
  qualityStatus: 'PENDING_INSPECTION' | 'PASSED' | 'FAILED' | 'CONDITIONAL';
  inspectedById?: string;
  inspectedAt?: string;
  notes?: string;
  purchaseOrderItem?: {
    id: string;
    quantity: number;
    unitPrice?: number;
    receivedQty: number;
    item: {
      id: string;
      name: string;
      sku: string;
    };
  };
  shipmentItem?: {
    id: string;
    quantity: number;
    item: {
      id: string;
      name: string;
      sku: string;
    };
  };
}

export interface Discrepancy {
  id: string;
  goodsReceiptId: string;
  type: 'QUANTITY_MISMATCH' | 'QUALITY_ISSUE' | 'DAMAGED_GOODS' | 'WRONG_ITEM' | 'MISSING_DOCUMENTATION' | 'OTHER';
  description: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CLOSED';
  resolution?: string;
  resolvedById?: string;
  resolvedAt?: string;
  createdAt: string;
}

export interface GoodsReceipt {
  id: string;
  receiptNumber: string;
  companyId: string;
  purchaseOrderId?: string;
  shipmentId?: string;
  locationId: string;
  receivedById: string;
  receivedAt: string;
  status: 'PENDING' | 'PARTIALLY_RECEIVED' | 'COMPLETED' | 'CANCELLED';
  deliveryNoteNumber?: string;
  carrierName?: string;
  notes?: string;
  purchaseOrder?: {
    id: string;
    poNumber: string;
    status?: string;
    supplier?: {
      id: string;
      name: string;
    };
  };
  shipment?: {
    id: string;
    shipmentNumber: string;
    trackingNumber?: string;
    origin?: {
      id: string;
      name: string;
      code: string;
    };
    destination?: {
      id: string;
      name: string;
      code: string;
    };
  };
  location: {
    id: string;
    name: string;
    code: string;
  };
  receivedBy: {
    id: string;
    fullName: string;
  };
  items?: GoodsReceiptItem[];
  discrepancies?: Discrepancy[];
  _count?: {
    items: number;
    discrepancies: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface PendingPO {
  id: string;
  poNumber: string;
  status: string;
  expectedDate?: string;
  totalAmount?: number;
  supplier: {
    id: string;
    name: string;
  };
  items: {
    id: string;
    quantity: number;
    unitPrice?: number;
    receivedQty: number;
    item: {
      id: string;
      name: string;
      sku: string;
    };
  }[];
}

export interface GoodsReceiptDashboard {
  pending: number;
  partial: number;
  completedToday: number;
  openDiscrepancies: number;
  recentReceipts: GoodsReceipt[];
}

export interface QueryGoodsReceiptParams {
  purchaseOrderId?: string;
  shipmentId?: string;
  status?: string;
  locationId?: string;
  receivedFrom?: string;
  receivedTo?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface CreateGoodsReceiptDto {
  purchaseOrderId: string;
  locationId: string;
  deliveryNoteNumber?: string;
  carrierName?: string;
  notes?: string;
}

export interface CreateGoodsReceiptFromShipmentDto {
  shipmentId: string;
  locationId?: string;
  deliveryNoteNumber?: string;
  carrierName?: string;
  notes?: string;
}

export interface ReceiveItemDto {
  goodsReceiptItemId: string;
  receivedQty: number;
  rejectedQty?: number;
  rejectionReason?: string;
  batchNumber?: string;
  expiryDate?: string;
  serialNumbers?: string[];
  qualityStatus?: 'PASSED' | 'FAILED' | 'CONDITIONAL';
  notes?: string;
  // Material Certification Integration
  certificateId?: string;
  certificateNumber?: string;
  heatNumber?: string;
  lotNumber?: string;
}

export interface ReportDiscrepancyDto {
  type: 'QUANTITY_MISMATCH' | 'QUALITY_ISSUE' | 'DAMAGED_GOODS' | 'WRONG_ITEM' | 'MISSING_DOCUMENTATION' | 'OTHER';
  description: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
}

export interface ResolveDiscrepancyDto {
  resolution: string;
}

// API Functions
export const goodsReceiptApi = {
  // Get all goods receipts with filters
  getAll: async (params?: QueryGoodsReceiptParams) => {
    const response = await api.get<{
      data: GoodsReceipt[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>('/api/goods-receipts', { params });
    return response.data;
  },

  // Get goods receipt dashboard
  getDashboard: async () => {
    const response = await api.get<GoodsReceiptDashboard>('/api/goods-receipts/dashboard');
    return response.data;
  },

  // Get pending purchase orders
  getPendingPOs: async () => {
    const response = await api.get<PendingPO[]>('/api/goods-receipts/pending-pos');
    return response.data;
  },

  // Get single goods receipt
  getById: async (id: string) => {
    const response = await api.get<GoodsReceipt>(`/api/goods-receipts/${id}`);
    return response.data;
  },

  // Create goods receipt from PO
  createFromPO: async (data: CreateGoodsReceiptDto) => {
    const response = await api.post<GoodsReceipt>('/api/goods-receipts', data);
    return response.data;
  },

  // Create goods receipt from shipment
  createFromShipment: async (data: CreateGoodsReceiptFromShipmentDto) => {
    const response = await api.post<GoodsReceipt>('/api/goods-receipts/from-shipment', data);
    return response.data;
  },

  // Receive items
  receiveItems: async (id: string, items: ReceiveItemDto[]) => {
    const response = await api.post<GoodsReceipt>(`/api/goods-receipts/${id}/receive`, { items });
    return response.data;
  },

  // Report discrepancy
  reportDiscrepancy: async (id: string, data: ReportDiscrepancyDto) => {
    const response = await api.post<Discrepancy>(`/api/goods-receipts/${id}/discrepancies`, data);
    return response.data;
  },

  // Resolve discrepancy
  resolveDiscrepancy: async (receiptId: string, discrepancyId: string, data: ResolveDiscrepancyDto) => {
    const response = await api.patch<Discrepancy>(
      `/api/goods-receipts/${receiptId}/discrepancies/${discrepancyId}/resolve`,
      data
    );
    return response.data;
  },

  // Cancel receipt
  cancel: async (id: string) => {
    const response = await api.patch<GoodsReceipt>(`/api/goods-receipts/${id}/cancel`);
    return response.data;
  },
};
